import { Button, Card, Col, Divider, Flex, Image, Popconfirm, PopconfirmProps, Row, Tag, Typography } from 'antd';
import { observer } from 'mobx-react';
import React, { MouseEvent } from "react";
import Subjects from '../../models/subjects';
import { useStore } from "../../stores/root-store";
import TaskItemStore from "../../stores/task/task-item";
import TaskCloneStore from '../../stores/task/task-clone';

const { Text } = Typography;

const CloneTask: React.FC<{ task: TaskCloneStore, subject: Subjects }> = ({ task, subject }) => {

    const { subjectStores, fileService } = useStore();
    const newTicket = subjectStores[subject].newTicket;
    const ticketBar = subjectStores[subject].ticketBar;
    const { id } = task.data;

    const handleTicketAction = () => {
        newTicket.toggleTaskInTicket(id, ticketBar);
    }

    return <Card title={`Задача ${task.data.id}`} className='card'
        extra={<Button type="link" onClick={handleTicketAction}>{newTicket.contains(id) ? "Убрать из варианта" : "Добавить в вариант"}</Button>}
    >
        <Card.Meta
            description={
                <div>
                    <Tag color="magenta">{task.data.theme?.name}</Tag>
                    <Tag color='green'>{task.data.source.name}</Tag>
                    <Tag color="cyan">Номер в КИМ {task.data.numberInKim?.name}</Tag>
                    {task.data.tags.map(tag => <Tag key={tag}>{tag}</Tag>)}
                </div>
            }
        />
        {task.data.photoIds &&
            <Flex wrap="wrap" gap="small" style={{ marginTop: "20px" }}>
                <Image.PreviewGroup >
                    <Image key={task.data.photoIds[0]} width={400} src={fileService.getFileUrl(task.data.photoIds[0])} />
                </Image.PreviewGroup>
            </Flex>
        }
    </Card>;
}

const TaskItem: React.FC<{ taskProps: TaskItemStore, subject: Subjects }> = ({ taskProps, subject }) => {

    const { subjectStores, fileService } = useStore();
    const ticketBar = subjectStores[subject].ticketBar;
    const newTicket = subjectStores[subject].newTicket;
    const taskList = subjectStores[subject].taskList;

    const { id, theme, source, numberInKim, withSolution, text, tags, photoIds, solutionPhotoIds, answer } = taskProps.data;
    const tagItems = [];

    const handleExpand = (event: MouseEvent<HTMLAnchorElement>) => {
        taskProps.toggle();
    }

    const handleClonesExpand = (event: MouseEvent<HTMLAnchorElement>) => {
        taskProps.toggleClones();
    }

    const handleDelete: PopconfirmProps['onConfirm'] = (e) => {
        taskList.deleteTask(taskProps.data.id);
    }

    const handleTicketAction = () => {
        newTicket.toggleTaskInTicket(id, ticketBar);
    }

    for (let tag of (tags || [])) {
        tagItems.push(<Tag key={tag}>{tag}</Tag>);
    }

    const photoImages = [];
    const solutionImages = [];

    for (let photo of (photoIds || [])) {
        photoImages.push(<Image key={photo} width={400} src={fileService.getFileUrl(photo)} />);
    }

    for (let photo of (solutionPhotoIds || [])) {
        solutionImages.push(<Image key={photo} width={300} src={fileService.getFileUrl(photo)} />);
    }

    return (
        <div>
            <Row align="top">
                <Col span={21}>
                    <Tag color="purple" key={id} title='Номер задачи'>{id}</Tag>
                    <Tag color="magenta">{theme?.name}</Tag>
                    <Tag color="green">{source?.name}</Tag>
                    <Tag color="cyan">Номер в КИМ {numberInKim?.name}</Tag>
                    {answer && <Tag color="gold">Ответ: {answer}</Tag>}
                    {withSolution && <Tag color="geekblue">Есть решение</Tag>}
                    {tagItems}
                    {(photoImages.length > 0) &&
                        <Flex wrap="wrap" gap="small" style={{ marginTop: "20px" }}>
                            <Image.PreviewGroup>
                                {photoImages}
                            </Image.PreviewGroup>
                        </Flex>
                    }
                </Col>
                <Col span={3}>
                    <Flex vertical align='flex-start' justify='start'>
                        <Button type='link' onClick={handleExpand}>{taskProps.expanded ? "Скрыть" : "Раскрыть"}</Button>
                        <Button type='link' href={`/math/tasks/edit/${id}`}>Редактировать</Button>
                        <Button type='link' onClick={handleTicketAction}>{newTicket.contains(id) ? "Убрать из варианта" : "Добавить в вариант"}</Button>
                        <Button type='link' onClick={handleClonesExpand}>{taskProps.clonesExpanded ? "Скрыть клоны" : "Показать клоны"}</Button>
                        <Popconfirm
                            title="Вы уверены, что хотите удалить задачу?"
                            description="Отменить это действие будет невозможно"
                            onConfirm={handleDelete}
                            okText="Да"
                            cancelText="Нет"
                        >
                            <Button type='link' danger>Удалить</Button>
                        </Popconfirm>
                    </Flex>
                </Col>
            </Row>
            {taskProps.expanded &&
                <Row>
                    <Col>
                        <Divider orientation="left">Распознанный текст</Divider>
                        <div>{text}</div>
                    </Col>
                    {(solutionImages.length > 0) &&
                        <Row>
                            <Divider orientation="left">Решение</Divider>
                            <Flex wrap="wrap" gap="small">
                                <Image.PreviewGroup>
                                    {solutionImages}
                                </Image.PreviewGroup>
                            </Flex>
                        </Row>
                    }
                </Row>
            }
            {taskProps.clonesExpanded &&
                <Row>
                    <Divider orientation="left">Похожие</Divider>
                    <Flex wrap="wrap" gap="middle" align="start">
                        {taskProps.clones?.length == 0 && <Text>Нет клонов</Text>}
                        {taskProps.clones.map(t => <CloneTask key={t.data.id} task={t} subject={subject}/>)}
                    </Flex>
                </Row>
            }
        </div>
    );
};

export default observer(TaskItem);